<style scoped lang="scss">
.container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.page-bg {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #f0f1f5;
    background-image:
        radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.03) 0%, transparent 50%);
    z-index: -1;
}

.main-content {
    flex: 1;
    overflow-y: auto;
    padding: 6rem 2rem 4rem 2rem;
}

.doc-wrapper {
    max-width: 900px;
    margin: 0 auto;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 1px 3px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}

.doc-header {
    padding: 2rem 2rem 1.5rem;
    border-bottom: 1px solid #f0f0f2;

    h1 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1a1a2e;
        margin: 0 0 0.5rem 0;
        display: flex;
        align-items: center;
        gap: 0.75rem;

        &::before {
            content: '';
            width: 4px;
            height: 1.25rem;
            background: #6366f1;
            border-radius: 2px;
        }
    }

    p {
        color: #94a3b8;
        font-size: 0.875rem;
        margin: 0;
        padding-left: 1.75rem;
    }
}

.toc-nav {
    padding: 1.25rem 2rem;
    border-bottom: 1px solid #f0f0f2;
    background: #fafafa;

    h3 {
        font-size: 0.6875rem;
        font-weight: 600;
        color: #94a3b8;
        margin: 0 0 0.875rem 0;
        text-transform: uppercase;
        letter-spacing: 0.08em;
    }

    .toc-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        a {
            display: inline-block;
            padding: 0.5rem 1rem;
            font-size: 0.8125rem;
            color: #475569;
            background: #fff;
            text-decoration: none;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            transition: all 0.15s ease;

            &:hover {
                color: #6366f1;
                border-color: #c7d2fe;
                background: #eef2ff;
                transform: translateY(-1px);
                box-shadow: 0 2px 4px rgba(99, 102, 241, 0.08);
            }
        }
    }
}

.api-section,
.auth-section,
.error-section {
    border-bottom: 1px solid #f0f0f2;

    &:last-child {
        border-bottom: none;
    }

    .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.125rem 2rem;
        background: linear-gradient(to right, #fafafb, #fff);
        border-bottom: 1px solid #f0f0f2;
        flex-wrap: wrap;
        gap: 0.75rem;

        h2 {
            font-size: 0.9375rem;
            font-weight: 600;
            color: #1e293b;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;

            &::before {
                content: '';
                width: 8px;
                height: 8px;
                background: #6366f1;
                border-radius: 50%;
            }
        }

        .method-badge {
            padding: 0.25rem 0.625rem;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            border-radius: 4px;

            &.get {
                background: #ecfdf5;
                color: #059669;
                border: 1px solid #a7f3d0;
            }

            &.post {
                background: #eff6ff;
                color: #2563eb;
                border: 1px solid #bfdbfe;
            }
        }

        .api-url {
            font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
            font-size: 0.75rem;
            color: #6366f1;
            background: #eef2ff;
            padding: 0.3125rem 0.75rem;
            border-radius: 4px;
            font-weight: 500;
        }
    }

    .section-body {
        padding: 1.5rem 2rem;
    }
}

.info-card {
    background: #f8f9fb;
    border-left: 3px solid #6366f1;
    padding: 1rem 1.25rem;
    margin-bottom: 1.25rem;
    border-radius: 0 6px 6px 0;

    h4 {
        font-size: 0.75rem;
        font-weight: 600;
        color: #64748b;
        margin: 0 0 0.75rem 0;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .info-grid {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.5rem 1.5rem;
        font-size: 0.875rem;

        .label {
            color: #94a3b8;
        }

        .value {
            color: #334155;
            font-weight: 500;
        }
    }
}

.param-table-wrapper {
    margin-bottom: 1.25rem;

    h4 {
        font-size: 0.875rem;
        font-weight: 600;
        color: #334155;
        margin: 0 0 0.75rem 0;
    }

    table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 0.8125rem;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        overflow: hidden;

        th {
            background: #f8f9fb;
            padding: 0.6875rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #475569;
            border-bottom: 1px solid #e2e8f0;
            white-space: nowrap;
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.02em;
        }

        td {
            padding: 0.625rem 1rem;
            color: #475569;
            border-bottom: 1px solid #f1f5f9;
            vertical-align: top;
        }

        tr:last-child td {
            border-bottom: none;
        }

        tr:hover td {
            background: #fafbfc;
        }

        code {
            font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
            background: #f1f5f9;
            padding: 0.1875rem 0.5rem;
            font-size: 0.75rem;
            color: #6366f1;
            border-radius: 4px;
            font-weight: 500;
        }

        .required {
            color: #ef4444;
            font-weight: 600;
        }

        .optional {
            color: #94a3b8;
        }
    }
}

.code-block {
    background: #1e1e2e;
    margin-bottom: 1.25rem;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .code-header {
        padding: 0.625rem 1.25rem;
        background: #181825;
        border-bottom: 1px solid #313244;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        &::before {
            content: '';
            display: flex;
            gap: 6px;
            margin-right: 0.75rem;

            span {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: #f38ba8;

                &:nth-child(2) {
                    background: #f9e2af;
                }

                &:nth-child(3) {
                    background: #a6e3a1;
                }
            }
        }

        >span {
            font-size: 0.75rem;
            color: #6c7086;
            font-weight: 500;
        }
    }

    pre {
        margin: 0;
        padding: 1.25rem 1.5rem;
        overflow-x: auto;

        code {
            font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
            font-size: 0.8125rem;
            line-height: 1.7;
            color: #cdd6f4;
        }
    }
}

.auth-steps {
    counter-reset: step;

    .step-item {
        display: flex;
        gap: 1rem;
        padding: 1.125rem 0;
        border-bottom: 1px dashed #e2e8f0;

        &:last-child {
            border-bottom: none;
        }

        &::before {
            counter-increment: step;
            content: counter(step);
            min-width: 1.75rem;
            height: 1.75rem;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8125rem;
            font-weight: 700;
            border-radius: 6px;
            box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);
        }

        .step-content {
            flex: 1;
            padding-top: 0.125rem;

            h4 {
                font-size: 0.9375rem;
                font-weight: 600;
                color: #1e293b;
                margin: 0 0 0.375rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #64748b;
                margin: 0;
                line-height: 1.65;
            }
        }
    }
}

.formula-box {
    background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%);
    border: 1px solid #fde68a;
    padding: 1.125rem 1.5rem;
    margin-top: 1.25rem;
    border-radius: 8px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '⚡';
        position: absolute;
        top: 0.75rem;
        right: 1rem;
        font-size: 1.25rem;
        opacity: 0.3;
    }

    p {
        font-size: 0.8125rem;
        color: #92400e;
        margin: 0 0 0.625rem 0;
        font-weight: 600;
    }

    code {
        display: block;
        font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
        font-size: 0.875rem;
        color: #78350f;
        padding: 0.5rem 0;
        line-height: 1.7;
        font-weight: 500;
    }
}

.error-section {
    .section-header {
        background: linear-gradient(to right, #fef2f2, #fff);

        h2::before {
            background: #ef4444;
        }
    }

    table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 0.8125rem;
        border: 1px solid #fecaca;
        border-radius: 6px;
        overflow: hidden;

        th {
            background: #fef2f2;
            padding: 0.625rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #991b1b;
            border-bottom: 1px solid #fecaca;
        }

        td {
            padding: 0.5625rem 1rem;
            color: #475569;
            border-bottom: 1px solid #fef2f2;
        }

        tr:last-child td {
            border-bottom: none;
        }

        tr:hover td {
            background: #fef9f9;
        }

        code {
            font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
            background: #fee2e2;
            padding: 0.1875rem 0.5rem;
            font-size: 0.75rem;
            color: #dc2626;
            border-radius: 4px;
            font-weight: 500;
        }
    }
}

@media (max-width: 768px) {
    .main-content {
        padding: 6rem 1rem 4rem 1rem;
    }

    .doc-wrapper {
        border-radius: 6px;
    }

    .doc-header {
        padding: 1.5rem 1.25rem 1.25rem;

        h1 {
            font-size: 1.25rem;
        }

        p {
            font-size: 0.8125rem;
        }
    }

    .toc-nav {
        padding: 1rem 1.25rem;

        .toc-list {
            gap: 0.375rem;

            a {
                padding: 0.4375rem 0.75rem;
                font-size: 0.75rem;
            }
        }
    }

    .api-section,
    .auth-section,
    .error-section {
        .section-header {
            padding: 1rem 1.25rem;
            flex-direction: column;
            align-items: flex-start !important;

            .api-url {
                font-size: 0.6875rem;
                word-break: break-all;
            }
        }

        .section-body {
            padding: 1.125rem 1.25rem;
        }
    }

    .info-grid {
        grid-template-columns: 1fr !important;
    }

    .code-block pre {
        padding: 1rem;

        code {
            font-size: 0.75rem;
        }
    }

    table {
        font-size: 0.75rem !important;

        th,
        td {
            padding: 0.5rem 0.75rem !important;
        }
    }
}
</style>
<template>
    <div class="container">
        <div style="display: flex;flex-direction: column;height: 100%;">
            <IndexHeader />
            <div class="page-bg"></div>
            <div class="main-content">
                <div class="doc-wrapper">
                    <div class="doc-header">
                        <h1>API 接口文档</h1>
                        <p>晓风授权系统开放接口 v1.0</p>
                    </div>

                    <div class="toc-nav">
                        <h3>目录导航</h3>
                        <div class="toc-list">
                            <a href="#auth">接口认证</a>
                            <a href="#account">账户信息</a>
                            <a href="#category">商品分类</a>
                            <a href="#goods-list">商品列表</a>
                            <a href="#goods-info">商品信息</a>
                            <a href="#goods-buy">购买商品</a>
                            <a href="#errors">错误码</a>
                        </div>
                    </div>

                    <div id="auth" class="auth-section">
                        <div class="section-header">
                            <h2>接口认证说明</h2>
                        </div>
                        <div class="section-body">
                            <div class="auth-steps">
                                <div class="step-item">
                                    <div class="step-content">
                                        <h4>获取密钥</h4>
                                        <p>登录后进入 个人中心 → 秘钥管理 获取 AppId 和 AppSecret</p>
                                    </div>
                                </div>
                                <div class="step-item">
                                    <div class="step-content">
                                        <h4>添加请求头</h4>
                                        <p>所有请求需要在 Header 中添加以下三个参数：AppId、AppTimestamp、AppToken</p>
                                    </div>
                                </div>
                                <div class="step-item">
                                    <div class="step-content">
                                        <h4>计算 Token</h4>
                                        <p>使用 SHA1 算法对拼接字符串进行加密生成 Token</p>
                                    </div>
                                </div>
                            </div>
                            <div class="formula-box">
                                <p>Token 计算公式：</p>
                                <code>AppToken = sha1(AppId + AppSecret + 请求URI + AppTimestamp)</code>
                            </div>
                        </div>
                    </div>

                    <div id="account" class="api-section">
                        <div class="section-header">
                            <h2>1. 账户信息</h2>
                            <span class="method-badge get">GET</span>
                            <span class="api-url">/openapi/customer/CustomerAccount/Show</span>
                        </div>
                        <div class="section-body">
                            <div class="info-card">
                                <h4>基本信息</h4>
                                <div class="info-grid">
                                    <span class="label">认证方式：</span>
                                    <span class="value">无需认证</span>
                                    <span class="label">环境URL：</span>
                                    <span class="value">http://商城域名</span>
                                </div>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>响应示例 - 成功 (200)</span></div>
                                <pre><code>{
  "code": 0,
  "message": "",
  "data": {
    "balance": 88.88,
    "name": "yile"
  }
}</code></pre>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>返回参数说明</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>参数名</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>code</td>
                                            <td>Integer</td>
                                            <td>状态码，0表示成功</td>
                                        </tr>
                                        <tr>
                                            <td>message</td>
                                            <td>String</td>
                                            <td>错误信息</td>
                                        </tr>
                                        <tr>
                                            <td>data</td>
                                            <td>Object</td>
                                            <td>账户信息</td>
                                        </tr>
                                        <tr>
                                            <td>data.balance</td>
                                            <td>Number</td>
                                            <td>余额</td>
                                        </tr>
                                        <tr>
                                            <td>data.name</td>
                                            <td>String</td>
                                            <td>商品名称</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div id="category" class="api-section">
                        <div class="section-header">
                            <h2>2. 商品分类</h2>
                            <span class="method-badge get">GET</span>
                            <span class="api-url">/openapi/customer/Goods/CategoryList</span>
                        </div>
                        <div class="section-body">
                            <div class="info-card">
                                <h4>基本信息</h4>
                                <div class="info-grid">
                                    <span class="label">认证方式：</span>
                                    <span class="value">无需认证</span>
                                    <span class="label">环境URL：</span>
                                    <span class="value">http://商城域名</span>
                                </div>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>响应示例 - 成功 (200)</span></div>
                                <pre><code>{
  "code": 0,
  "message": "",
  "data": [
    {
      "id": 100,
      "name": "特惠商品",
      "parent_id": 0,
      "parent_infos": [],
      "weight": 2
    },
    {
      "id": 101,
      "name": "视频会员",
      "parent_id": 0,
      "parent_infos": [
        { "id": 103, "name": "腾讯视频", ... },
        { "id": 102, "name": "爱奇艺", ... }
      ],
      "weight": 1
    }
  ]
}</code></pre>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>返回参数说明</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>参数名</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>data.id</td>
                                            <td>Integer</td>
                                            <td>分类ID</td>
                                        </tr>
                                        <tr>
                                            <td>data.name</td>
                                            <td>String</td>
                                            <td>分类名称</td>
                                        </tr>
                                        <tr>
                                            <td>data.parent_id</td>
                                            <td>Integer</td>
                                            <td>上级分类ID</td>
                                        </tr>
                                        <tr>
                                            <td>data.parent_infos</td>
                                            <td>Array</td>
                                            <td>二级分类列表</td>
                                        </tr>
                                        <tr>
                                            <td>data.weight</td>
                                            <td>Integer</td>
                                            <td>排序权重</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div id="goods-list" class="api-section">
                        <div class="section-header">
                            <h2>3. 商品列表</h2>
                            <span class="method-badge post">POST</span>
                            <span class="api-url">/openapi/customer/Goods/List</span>
                        </div>
                        <div class="section-body">
                            <div class="info-card">
                                <h4>基本信息</h4>
                                <div class="info-grid">
                                    <span class="label">Content-Type：</span>
                                    <span class="value">application/json</span>
                                    <span class="label">认证方式：</span>
                                    <span class="value">无需认证</span>
                                </div>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>请求参数</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>参数名</th>
                                            <th>是否必填</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><code>goods_category_id</code></td>
                                            <td class="optional">否</td>
                                            <td>Integer</td>
                                            <td>商品分类ID</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>请求示例</span></div>
                                <pre><code>{
  "goods_category_id": 100
}</code></pre>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>响应示例 - 成功 (200)</span></div>
                                <pre><code>{
  "code": 0,
  "message": "",
  "data": [
    {
      "id": 173,
      "name": "腾讯视频",
      "price": 101,
      "stock": -1,
      "unit": "1",
      "is_card_code": 2,
      "is_close": 2
    }
  ]
}</code></pre>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>数据字段说明</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>字段名</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>id</td>
                                            <td>Integer</td>
                                            <td>商品ID</td>
                                        </tr>
                                        <tr>
                                            <td>name</td>
                                            <td>String</td>
                                            <td>商品名称</td>
                                        </tr>
                                        <tr>
                                            <td>price</td>
                                            <td>Float</td>
                                            <td>商品价格</td>
                                        </tr>
                                        <tr>
                                            <td>stock</td>
                                            <td>Integer</td>
                                            <td>库存（-1表示无限）</td>
                                        </tr>
                                        <tr>
                                            <td>unit</td>
                                            <td>String</td>
                                            <td>商品单位</td>
                                        </tr>
                                        <tr>
                                            <td>is_card_code</td>
                                            <td>Integer</td>
                                            <td>是否卡密商品 1是2否</td>
                                        </tr>
                                        <tr>
                                            <td>is_close</td>
                                            <td>Integer</td>
                                            <td>是否关闭下单 1是2否</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div id="goods-info" class="api-section">
                        <div class="section-header">
                            <h2>4. 商品信息</h2>
                            <span class="method-badge post">POST</span>
                            <span class="api-url">/openapi/customer/Goods/Show</span>
                        </div>
                        <div class="section-body">
                            <div class="info-card">
                                <h4>基本信息</h4>
                                <div class="info-grid">
                                    <span class="label">Content-Type：</span>
                                    <span class="value">application/json</span>
                                    <span class="label">认证方式：</span>
                                    <span class="value">无需认证</span>
                                </div>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>请求参数</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>参数名</th>
                                            <th>是否必填</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><code>goods_id</code></td>
                                            <td class="required">是</td>
                                            <td>Integer</td>
                                            <td>商品ID</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>请求示例</span></div>
                                <pre><code>{
  "goods_id": 173
}</code></pre>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>响应示例 - 成功 (200)</span></div>
                                <pre><code>{
  "code": 0,
  "message": "",
  "data": {
    "id": 173,
    "name": "腾讯视频",
    "price": 101,
    "stock": -1,
    "unit": "1",
    "buy_max_limit": 100,
    "buy_min_limit": 1,
    "is_batch": 2,
    "is_card_code": 2,
    "is_close": 2,
    "buy_params": [
      { "key": "QQ", "name": "QQ", "type": 1, "description": "请输入QQ号码" }
    ]
  }
}</code></pre>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>主要字段说明</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>字段名</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>price</td>
                                            <td>Float</td>
                                            <td>商品价格</td>
                                        </tr>
                                        <tr>
                                            <td>stock</td>
                                            <td>Integer</td>
                                            <td>库存（-1表示无限）</td>
                                        </tr>
                                        <tr>
                                            <td>buy_max_limit</td>
                                            <td>Integer</td>
                                            <td>最大下单数量</td>
                                        </tr>
                                        <tr>
                                            <td>buy_min_limit</td>
                                            <td>Integer</td>
                                            <td>最小下单数量</td>
                                        </tr>
                                        <tr>
                                            <td>is_batch</td>
                                            <td>Integer</td>
                                            <td>是否允许批量下单 1能2否</td>
                                        </tr>
                                        <tr>
                                            <td>is_card_code</td>
                                            <td>Integer</td>
                                            <td>是否卡密商品 1是2否</td>
                                        </tr>
                                        <tr>
                                            <td>buy_params</td>
                                            <td>Array</td>
                                            <td>下单参数配置</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div id="goods-buy" class="api-section">
                        <div class="section-header">
                            <h2>5. 购买商品</h2>
                            <span class="method-badge post">POST</span>
                            <span class="api-url">/openapi/customer/Goods/Buy</span>
                        </div>
                        <div class="section-body">
                            <div class="info-card">
                                <h4>基本信息</h4>
                                <div class="info-grid">
                                    <span class="label">Content-Type：</span>
                                    <span class="value">application/json</span>
                                    <span class="label">认证方式：</span>
                                    <span class="value">无需认证</span>
                                </div>
                            </div>
                            <div class="param-table-wrapper">
                                <h4>请求参数</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>参数名</th>
                                            <th>是否必填</th>
                                            <th>类型</th>
                                            <th>描述说明</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><code>goods_id</code></td>
                                            <td class="required">是</td>
                                            <td>Integer</td>
                                            <td>商品ID</td>
                                        </tr>
                                        <tr>
                                            <td><code>buy_number</code></td>
                                            <td class="required">是</td>
                                            <td>Integer</td>
                                            <td>购买数量</td>
                                        </tr>
                                        <tr>
                                            <td><code>buy_params</code></td>
                                            <td class="required">是</td>
                                            <td>Object</td>
                                            <td>购买参数Object</td>
                                        </tr>
                                        <tr>
                                            <td><code>customer_order_id</code></td>
                                            <td class="optional">否</td>
                                            <td>String</td>
                                            <td>客户单号（防重复）</td>
                                        </tr>
                                        <tr>
                                            <td><code>price</code></td>
                                            <td class="optional">否</td>
                                            <td>Float</td>
                                            <td>客户单价</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>请求示例</span></div>
                                <pre><code>{
  "goods_id": 102,
  "buy_number": 1,
  "buy_params": { "qq": "123456" },
  "customer_order_id": "12345",
  "price": 0.01
}</code></pre>
                            </div>
                            <div class="code-block">
                                <div class="code-header"><span>响应示例 - 成功 (200)</span></div>
                                <pre><code>{
  "code": 0,
  "message": "",
  "data": { "id": 100 }
}</code></pre>
                            </div>
                        </div>
                    </div>

                    <div id="errors" class="error-section">
                        <div class="section-header">
                            <h2>错误码说明</h2>
                        </div>
                        <div class="section-body">
                            <table>
                                <thead>
                                    <tr>
                                        <th>错误码</th>
                                        <th>含义</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>0</code></td>
                                        <td>成功</td>
                                    </tr>
                                    <tr>
                                        <td><code>1</code></td>
                                        <td>业务失败（商品不存在、参数无效等）</td>
                                    </tr>
                                    <tr>
                                        <td><code>100</code></td>
                                        <td>认证失败</td>
                                    </tr>
                                    <tr>
                                        <td><code>404</code></td>
                                        <td>接口不存在</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <IndexFooter />
        </div>
    </div>
</template>
<script>
import { defineComponent } from 'vue';
import IndexHeader from '@/components/IndexHeader.vue';
import IndexFooter from '@/components/IndexFooter.vue';

export default defineComponent({
    name: 'ApiDoc',
    components: {
        IndexHeader,
        IndexFooter
    }
})
</script>